// api/signalerDocument/signalerDocument.ts
import axios from "shared/api/axiosConfig";
import { encryptApiBodyFromJSON, decryptApiBodyToJson } from "shared/api/cryptApiBody";

export interface ReportDocumentData {
  paysEmetteur: string;
  typeDocument: string;
  numeroDocument: string;
  motif: string;
  companyName: string | null;
}

export interface ReportedDocument {
  id: number;
  paysEmetteur: string;
  typeDocument: string;
  numeroDocument: string;
  motif: string;
  companyName: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  total?: number;
}

/**
 * Créer un signalement de document frauduleux
 */
export async function reportDocument(data: ReportDocumentData, userType = 'partner'): Promise<ApiResponse> {
  try {
    const response = await axios.post("/client/signaler-document/create", {
      encrypt: encryptApiBodyFromJSON({ ...data }),
      type: userType
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors du signalement du document:", e);
    throw e;
  }
}

/**
 * Récupérer les signalements (par entreprise ou tous)
 */
export async function getReportedDocuments(companyName?: string, userType = 'partner'): Promise<ApiResponse<ReportedDocument[]>> {
  try {
    const response = await axios.post("/client/signaler-document/list", {
      encrypt: encryptApiBodyFromJSON({ companyName }),
      type: userType
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors de la récupération des signalements:", e);
    throw e;
  }
}

/**
 * Supprimer un signalement (admin only), admin is base on user.partnerRole : admin | member
 */
export async function deleteReportedDocument(id: number, userType = 'partner'): Promise<ApiResponse> {
  try {
    const response = await axios.delete(`/client/signaler-document/delete/${id}`, {
      data: {
        type: userType
      }
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors de la suppression du signalement:", e);
    throw e;
  }
}

/**
 * Récupérer les détails d'un signalement spécifique
 */
export async function getReportedDocumentById(id: number, userType = 'partner'): Promise<ApiResponse<ReportedDocument>> {
  try {
    const response = await axios.post("/client/signaler-document/get-by-id", {
      encrypt: encryptApiBodyFromJSON({ id }),
      type: userType
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors de la récupération du signalement:", e);
    throw e;
  }
}

/**
 * Vérifier un signalement (admin only)
 */
export async function verifyReportedDocument(id: number, verifiedBy: string, userType = 'partner'): Promise<ApiResponse> {
  try {
    const response = await axios.put(`/client/signaler-document/verify/${id}`, {
      encrypt: encryptApiBodyFromJSON({ isAdmin: true, verifiedBy }),
      type: userType
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors de la vérification du signalement:", e);
    throw e;
  }
}

/**
 * Mettre à jour un signalement (admin only)
 */
export async function updateReportedDocument(id: number, data: Partial<ReportDocumentData>, userType = 'partner'): Promise<ApiResponse> {
  try {
    const response = await axios.put(`/client/signaler-document/update/${id}`, {
      encrypt: encryptApiBodyFromJSON({ isAdmin: true, ...data }),
      type: userType
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors de la mise à jour du signalement:", e);
    throw e;
  }
}

/**
 * Statistiques des signalements
 */
export async function getReportedDocumentsStats(userType = 'partner'): Promise<ApiResponse<{
  total: number;
  byMotif: Record<string, number>;
  byMonth: Record<string, number>;
}>> {
  try {
    const response = await axios.post("/client/signaler-document/stats", {
      encrypt: encryptApiBodyFromJSON({}),
      type: userType
    });

    // Decrypt the response if needed
    if (response.data?.encrypt) {
      return decryptApiBodyToJson(response.data.encrypt);
    }
    return response.data;
  } catch (e) {
    console.error("Erreur lors de la récupération des statistiques:", e);
    throw e;
  }
}